import type { CSSProperties } from "react";
import classNames from "classnames";
import { TagCloseIcon } from "@design-system/Tag/TagCloseIcon";
import {
    resolveAnatomyBindings,
    type ResolvedAnatomyBinding,
} from "./componentAnatomy";
import {
    bindingReader,
    Part,
    type AnatomyPartHighlight,
} from "./componentAnatomyParts";
import { tagAnatomy } from "./componentAnatomyTag";
import styles from "./ComponentAnatomyView.module.scss";

/**
 * What stands inside the tags input on the x-ray plate. The playground passes
 * its live values and placeholder, so the schematic draws the same chips.
 */
export interface TagsInputSchematicContent {
    values: readonly string[];
    placeholder: string;
}

const defaultContent: TagsInputSchematicContent = {
    values: ["Payroll", "Invoices"],
    placeholder: "Type or paste values",
};

// Chips are drawn from the Tag anatomy (lg, primary, resting) rather than
// restating Tag's tokens in this spec.
const chipVariant = tagAnatomy.variants.find(
    (variant) => variant.id === "primary",
);
const readChip = bindingReader(
    chipVariant === undefined
        ? []
        : resolveAnatomyBindings({
              spec: tagAnatomy,
              variant: chipVariant,
              size: tagAnatomy.sizes?.find((size) => size.id === "lg"),
              stateId: "resting",
          }),
);

const chipStyle: CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    boxSizing: "border-box",
    minWidth: 0,
    maxWidth: "100%",
    height: readChip("background", "height"),
    paddingInline: readChip("background", "padding-inline"),
    paddingRight: readChip("background", "padding-right (with X)"),
    gap: readChip("background", "gap"),
    borderRadius: readChip("background", "border-radius"),
    backgroundColor: readChip("background", "background-color"),
    borderStyle: "solid",
    borderWidth: readChip("border", "border-width"),
    borderColor: readChip("border", "border-color"),
    color: readChip("label", "color"),
    fontSize: readChip("label", "font-size"),
    fontWeight: readChip("label", "font-weight"),
    lineHeight: readChip("label", "line-height"),
    letterSpacing: readChip("label", "letter-spacing"),
};

const chipLabelStyle: CSSProperties = {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
};

const chipXStyle: CSSProperties = {
    width: readChip("xButton", "width"),
    height: readChip("xButton", "height"),
    borderRadius: readChip("xButton", "border-radius"),
};

/** X-ray plate for TagsInput: 216px wide, so two chips share a row before the input wraps. */
export const TagsInputSchematic = ({
    bindings,
    highlight,
    content = defaultContent,
}: {
    bindings: readonly ResolvedAnatomyBinding[];
    highlight: AnatomyPartHighlight;
    content?: TagsInputSchematicContent;
}) => {
    const read = bindingReader(bindings);
    const radius = read("border", "border-radius");
    const borderWidth = read("border", "border-width");
    const ringSpread = read("focusRing", "box-shadow (spread)");
    const ringColor = read("focusRing", "box-shadow (color)");

    return (
        <span
            className={classNames(styles.schematic, styles.tagsInputSchematic)}
            style={{ minHeight: read("background", "min-height"), width: 216 }}
        >
            {ringSpread !== undefined && ringColor !== undefined && (
                <Part
                    partId="focusRing"
                    highlight={highlight}
                    className={styles.buttonRing}
                    hitInset={`calc(-1 * ${ringSpread})`}
                    style={{
                        borderRadius: radius,
                        boxShadow: `0 0 0 ${ringSpread} ${ringColor}`,
                    }}
                />
            )}
            <Part
                partId="border"
                highlight={highlight}
                className={styles.buttonBorder}
                style={{
                    borderColor: read("border", "border-color"),
                    borderWidth,
                    borderRadius: radius,
                }}
            />
            <Part
                partId="background"
                highlight={highlight}
                className={styles.buttonBackground}
                style={{
                    backgroundColor: read("background", "background-color"),
                    borderRadius: radius,
                }}
            />
            {/* The border is its own layer here, so the padding adds its
                width back to land content where the real .root does. */}
            <span
                className={styles.buttonContent}
                style={{
                    boxSizing: "border-box",
                    flexWrap: "wrap",
                    width: "100%",
                    height: "auto",
                    gap: read("background", "gap"),
                    paddingBlock: `calc(${read("background", "padding-block")} + ${borderWidth})`,
                    paddingInline: `calc(${read("background", "padding-inline")} + ${borderWidth})`,
                }}
            >
                {content.values.map((value) => (
                    <Part
                        key={value}
                        partId="chip"
                        highlight={highlight}
                        style={chipStyle}
                    >
                        <span style={chipLabelStyle}>{value}</span>
                        <span className={styles.tagXButton} style={chipXStyle}>
                            <TagCloseIcon />
                        </span>
                    </Part>
                ))}
                <Part
                    partId="label"
                    highlight={highlight}
                    className={styles.buttonLabel}
                    style={{
                        display: "inline-flex",
                        alignItems: "center",
                        flex: "1 1 auto",
                        width: 0,
                        minWidth: read("label", "min-width"),
                        height: read("label", "height"),
                        color: read("label", "color (placeholder)"),
                        fontSize: read("label", "font-size"),
                        fontWeight: read("label", "font-weight"),
                        lineHeight: read("label", "line-height"),
                        letterSpacing: read("label", "letter-spacing"),
                    }}
                >
                    {content.placeholder}
                </Part>
            </span>
        </span>
    );
};
